// ============================================================
// Lightweight Language Context — Passenger-facing pages only
// ============================================================

import { createContext, useContext, useState, useEffect, type ReactNode } from 'react';

export type Lang = 'en' | 'bn';

interface LanguageContextType {
  lang: Lang;
  setLang: (lang: Lang) => void;
  t: (key: string) => string;
  tr: (english: string, bangla: string) => string;
}

const STORAGE_KEY = 'starline_lang';

const LanguageContext = createContext<LanguageContextType | null>(null);

export function LanguageProvider({ children }: { children: ReactNode }) {
  const [lang, setLangState] = useState<Lang>(() => {
    try {
      const saved = localStorage.getItem(STORAGE_KEY);
      return (saved === 'bn' ? 'bn' : 'en') as Lang;
    } catch {
      return 'en';
    }
  });

  const setLang = (newLang: Lang) => {
    setLangState(newLang);
    try { localStorage.setItem(STORAGE_KEY, newLang); } catch { /* noop */ }
  };

  // Drive <html lang>. This is what activates the Bangla typographic tuning in
  // index.css, and it also tells screen readers and the browser's own hyphen /
  // line-break engine which script they are reading.
  useEffect(() => {
    document.documentElement.lang = lang;
  }, [lang]);

  // Sync across tabs
  useEffect(() => {
    const handler = (e: StorageEvent) => {
      if (e.key === STORAGE_KEY && (e.newValue === 'en' || e.newValue === 'bn')) {
        setLangState(e.newValue as Lang);
      }
    };
    window.addEventListener('storage', handler);
    return () => window.removeEventListener('storage', handler);
  }, []);

  const t = (key: string): string => {
    const entry = translations[key];
    if (!entry) return key;
    return entry[lang] || entry.en || key;
  };

  // Shared labels use `t`; page-specific sentences use `tr` so their natural
  // Bengali copy can stay beside the interface that owns it.
  const tr = (english: string, bangla: string): string => lang === 'bn' ? bangla : english;

  return (
    <LanguageContext.Provider value={{ lang, setLang, t, tr }}>
      {children}
    </LanguageContext.Provider>
  );
}

export function useLanguage() {
  const ctx = useContext(LanguageContext);
  if (!ctx) throw new Error('useLanguage must be inside LanguageProvider');
  return ctx;
}

// ── Translations ─────────────────────────────────────────────
// Only passenger-facing strings. Staff dashboards stay English.

const translations: Record<string, Record<Lang, string>> = {
  // SearchForm
  'from': { en: 'From', bn: 'কোথা থেকে' },
  'to': { en: 'To', bn: 'কোথায়' },
  'date': { en: 'Date', bn: 'তারিখ' },
  'passengers': { en: 'Passengers', bn: 'যাত্রী' },
  'search': { en: 'Search', bn: 'খুঁজুন' },
  'same_origin_dest': { en: 'Origin and destination cannot be the same', bn: 'একই জায়গায় যাওয়া-আসা হবে না' },

  // SearchResults
  'no_trips': { en: 'No Trips Found', bn: 'কোনো যাত্রা পাওয়া যায়নি' },
  'no_trips_desc': { en: 'We couldn\'t find any trips for this route and date.', bn: 'এই পথে ও তারিখে কোনো বাস পাওয়া যায়নি।' },
  'try_nearby_dates': { en: 'Try nearby dates:', bn: 'অন্য তারিখ চেষ্টা করুন:' },
  'select_seats': { en: 'Select Seats', bn: 'সিট বাছাই' },
  'show_all_types': { en: 'Show All Types', bn: 'সব ধরন দেখুন' },
  'seats_left': { en: 'seats left', bn: 'টি সিট বাকি' },
  'trips_available': { en: 'trips available', bn: 'টি বাস পাওয়া গেছে' },

  // SeatSelection
  'passenger_details': { en: 'Passenger Details', bn: 'যাত্রীর তথ্য' },
  'full_name': { en: 'Full Name', bn: 'নাম' },
  'phone': { en: 'Phone', bn: 'ফোন' },
  'email': { en: 'Email', bn: 'ইমেইল' },
  'fare_summary': { en: 'Fare Summary', bn: 'ভাড়ার সারাংশ' },
  'proceed_checkout': { en: 'Proceed to Checkout', bn: 'এগিয়ে যান' },
  'creating_booking': { en: 'Creating Booking...', bn: 'টিকিট সংরক্ষণ করা হচ্ছে...' },
  'select_n_seats': { en: 'Select seat(s) to continue', bn: 'সিট বাছাই করুন' },
  'enter_name': { en: 'Enter passenger name', bn: 'যাত্রীর নাম লিখুন' },
  'secure': { en: 'Secure', bn: 'নিরাপদ' },
  'support_247': { en: '24/7 Support', bn: 'সবসময় সাহায্য' },

  // Checkout
  'secure_checkout': { en: 'Secure Checkout', bn: 'নিরাপদে মূল্য পরিশোধ' },
  'booking_not_found': { en: 'Booking Not Found', bn: 'টিকিটের তথ্য পাওয়া যায়নি' },
  'booking_not_found_desc': { en: 'We couldn\'t find this booking. It may have expired.', bn: 'টিকিটের তথ্যটি পাওয়া যাচ্ছে না। সংরক্ষণের সময় শেষ হয়ে থাকতে পারে।' },
  'reservation_expired': { en: 'Reservation expired. Please search again.', bn: 'সময় শেষ! আবার খুঁজুন।' },
  'complete_payment_in': { en: 'Complete payment within', bn: 'এই সময়ের মধ্যে মূল্য পরিশোধ করুন' },
  'seats_held': { en: 'Seats held for limited time', bn: 'আপনার সিট সীমিত সময়ের জন্য ধরে রাখা আছে' },
  'payment_method': { en: 'Payment Method', bn: 'পরিশোধের মাধ্যম' },
  'confirm_pay': { en: 'Confirm & Pay', bn: 'নিশ্চিত করে মূল্য পরিশোধ করুন' },
  'processing': { en: 'Processing...', bn: 'কাজটি সম্পন্ন হচ্ছে...' },
  'trust_badge': { en: 'Secured by SSLCommerz • bKash • Nagad • Visa/MC', bn: 'SSLCommerz-এর নিরাপদ পরিশোধ ব্যবস্থা • bKash • Nagad • Visa/MC' },
  'need_help': { en: 'Need help with payment?', bn: 'মূল্য পরিশোধে সহায়তা দরকার?' },
  'hotline': { en: 'Hotline: +8801973-259609', bn: 'হটলাইন: +8801973-259609' },
  'booking_summary': { en: 'Booking Summary', bn: 'টিকিটের সারসংক্ষেপ' },
  'search_trips': { en: 'Search Trips', bn: 'বাসের টিকিট খুঁজুন' },

  // PaymentSuccess
  'booking_confirmed': { en: 'Booking Confirmed!', bn: 'টিকিট নিশ্চিত হয়েছে!' },
  'booking_confirmed_desc': { en: 'Your payment was processed and your e-ticket is ready.', bn: 'মূল্য পরিশোধ সফল হয়েছে এবং আপনার ই-টিকিট প্রস্তুত।' },
  'view_eticket': { en: 'View E-Ticket', bn: 'ই-টিকেট দেখুন' },
  'go_dashboard': { en: 'Go to Dashboard', bn: 'আমার যাত্রায় যান' },
  'payment_successful': { en: 'Payment Successful!', bn: 'মূল্য পরিশোধ সফল হয়েছে!' },
  'confirming_booking': { en: 'Confirming your booking — this may take a moment...', bn: 'টিকিট নিশ্চিত করা হচ্ছে—একটু সময় লাগতে পারে...' },
  'verifying_gateway': { en: 'Verifying payment with gateway', bn: 'পরিশোধের তথ্য যাচাই করা হচ্ছে' },
  'payment_received': { en: 'Payment Received', bn: 'পরিশোধ গ্রহণ করা হয়েছে' },
  'payment_received_desc': { en: 'Your payment was received but booking confirmation is taking longer than expected.', bn: 'আপনার পরিশোধ গ্রহণ করা হয়েছে, তবে টিকিট নিশ্চিত হতে স্বাভাবিকের চেয়ে বেশি সময় লাগছে।' },
  'retry_check': { en: 'Retry Confirmation Check', bn: 'আবার চেক করুন' },

  // PaymentFail
  'payment_failed': { en: 'Payment Failed', bn: 'মূল্য পরিশোধ করা যায়নি' },
  'payment_failed_desc': { en: 'Your payment could not be processed. Your booking is still reserved — you can try again.', bn: 'মূল্য পরিশোধ সম্পন্ন হয়নি। আপনার আসনটি এখনো সংরক্ষিত আছে—আবার চেষ্টা করতে পারেন।' },
  'money_deducted_title': { en: '💰 Payment deducted but no ticket?', bn: '💰 টাকা কেটে গেছে কিন্তু টিকেট পাননি?' },
  'money_deducted_desc': { en: 'If money was deducted from your bKash/Nagad/Card but no ticket was issued:', bn: 'যদি আপনার bKash/Nagad/Card থেকে টাকা কেটে যায় কিন্তু টিকেট না আসে:' },
  'refund_auto': { en: 'Auto-refunded within 3-7 business days', bn: 'টাকা ৩-৭ কর্মদিবসে স্বয়ংক্রিয়ভাবে ফেরত হবে' },
  'call_if_no_refund': { en: 'Call hotline if not refunded', bn: 'ফেরত না হলে হটলাইনে কল করুন' },
  'save_txn_id': { en: 'Keep your Transaction ID', bn: 'Transaction ID সংরক্ষণ করুন' },
  'try_again': { en: 'Try Again', bn: 'আবার চেষ্টা করুন' },
  'pay_at_counter': { en: 'Pay at Counter', bn: 'কাউন্টারে মূল্য পরিশোধ করুন' },
  'go_home': { en: 'Go Home', bn: 'প্রথম পাতায় যান' },

  // PaymentCancel
  'payment_cancelled': { en: 'Payment Cancelled', bn: 'মূল্য পরিশোধ বাতিল হয়েছে' },
  'payment_cancelled_desc': { en: 'You cancelled the payment. Don\'t worry — your seat is still reserved for a while.', bn: 'আপনি মূল্য পরিশোধ বাতিল করেছেন। চিন্তার কিছু নেই—আসনটি আরও কিছুক্ষণ সংরক্ষিত থাকবে।' },
  'seat_held_15': { en: 'Your seat is reserved for 15 minutes', bn: 'আপনার সিট ১৫ মিনিট ধরে রাখা আছে' },
  'seat_held_15_desc': { en: 'Complete payment before it expires.', bn: 'সময় শেষ হওয়ার আগে পেমেন্ট করুন।' },
  'complete_payment': { en: 'Complete Payment', bn: 'মূল্য পরিশোধ সম্পন্ন করুন' },
  'need_help_short': { en: 'Need help? +8801973-259609', bn: 'সাহায্য দরকার? +8801973-259609' },

  // Chat chips
  'chip_track': { en: 'Track issue', bn: 'অভিযোগের অবস্থা' },
  'chip_booking': { en: 'Booking help', bn: 'টিকিটে সহায়তা' },
  'chip_refund': { en: 'Refund', bn: 'টাকা ফেরত' },
  'chip_payment': { en: 'Payment', bn: 'পরিশোধের সমস্যা' },
  'chip_delay': { en: 'Bus delayed', bn: 'বাস দেরি করছে' },
  'chip_counter': { en: 'Counter info', bn: 'কাউন্টার তথ্য' },
  'chip_support': { en: 'Talk to support', bn: 'সহায়তাকর্মীর সঙ্গে কথা বলুন' },
  'chip_complaint': { en: 'Complaint', bn: 'অভিযোগ' },

  // General
  'total': { en: 'Total', bn: 'মোট' },

  // Booking disabled
  'booking_paused': { en: 'Online Booking Paused', bn: 'অনলাইন বুকিং সাময়িকভাবে বন্ধ' },
  'booking_paused_desc': { en: 'Online booking is temporarily paused. Please try again later or contact our counter.', bn: 'অনলাইন বুকিং সাময়িকভাবে বন্ধ আছে। পরে আবার চেষ্টা করুন অথবা কাউন্টারে যোগাযোগ করুন।' },
  'call_counter': { en: 'Call Counter', bn: 'কাউন্টারে কল করুন' },
  'back_to_home': { en: 'Back to Home', bn: 'প্রথম পাতায় ফিরুন' },

  // Ticket Details
  'ticket_details': { en: 'Ticket Details', bn: 'টিকেটের বিবরণ' },
  'booking_id': { en: 'Booking ID', bn: 'বুকিং আইডি' },
  'journey_date': { en: 'Journey Date', bn: 'যাত্রার তারিখ' },
  'departure_time': { en: 'Departure Time', bn: 'ছাড়ার সময়' },
  'arrival_time': { en: 'Est. Arrival', bn: 'আনুমানিক পৌঁছানো' },
  'boarding_point': { en: 'Boarding Point', bn: 'উঠার স্থান' },
  'dropping_point': { en: 'Dropping Point', bn: 'নামার স্থান' },
  'seat_numbers': { en: 'Seat(s)', bn: 'সিট' },
  'coach_name': { en: 'Coach', bn: 'কোচ' },
  'bus_type': { en: 'Bus Type', bn: 'বাসের ধরন' },
  'passenger_name': { en: 'Passenger', bn: 'যাত্রী' },
  'fare_breakdown': { en: 'Fare Breakdown', bn: 'ভাড়ার বিবরণ' },
  'per_seat': { en: 'per seat', bn: 'প্রতি সিট' },
  'grand_total': { en: 'Grand Total', bn: 'সর্বমোট' },
  'payment_status': { en: 'Payment Status', bn: 'পেমেন্ট অবস্থা' },
  'ticket_status': { en: 'Ticket Status', bn: 'টিকেটের অবস্থা' },
  'download_pdf': { en: 'Download PDF', bn: 'পিডিএফ ডাউনলোড' },
  'view_qr_code': { en: 'QR Code', bn: 'কিউআর কোড' },
  'cancel_booking': { en: 'Cancel Booking', bn: 'বুকিং বাতিল' },
  'continue_payment': { en: 'Continue Payment', bn: 'পেমেন্ট সম্পন্ন করুন' },
  'contact_support': { en: 'Contact Support', bn: 'সাপোর্টে যোগাযোগ' },
  'track_bus': { en: 'Track Bus', bn: 'বাস ট্র্যাক করুন' },
  'rate_trip': { en: 'Rate Trip', bn: 'ট্রিপ রেটিং দিন' },
  'ticket_not_found': { en: 'Ticket Not Found', bn: 'টিকেট পাওয়া যায়নি' },
  'ticket_not_found_desc': { en: 'This ticket does not exist or you do not have access.', bn: 'এই টিকেটটি নেই অথবা আপনার অ্যাক্সেস নেই।' },
  'back_to_bookings': { en: 'Back to My Bookings', bn: 'আমার বুকিংয়ে ফিরুন' },
  'money_deducted_help': { en: 'Payment deducted but no ticket?', bn: 'টাকা কেটে গেছে কিন্তু টিকেট পাননি?' },
  'money_deducted_help_desc': { en: 'If money was deducted but your booking shows pending, your refund will be processed automatically within 3-7 business days. Contact our hotline for immediate help.', bn: 'টাকা কেটে গেলে কিন্তু বুকিং পেন্ডিং থাকলে ৩-৭ কর্মদিবসে স্বয়ংক্রিয়ভাবে ফেরত হবে। তাৎক্ষণিক সাহায্যের জন্য হটলাইনে কল করুন।' },
  'confirmed': { en: 'Confirmed', bn: 'নিশ্চিত' },
  'pending_payment': { en: 'Pending Payment', bn: 'পেমেন্ট বাকি' },
  'cancelled': { en: 'Cancelled', bn: 'বাতিল' },
  'completed': { en: 'Completed', bn: 'সম্পন্ন' },
  'failed': { en: 'Failed', bn: 'ব্যর্থ' },
  'retry_payment': { en: 'Retry Payment', bn: 'আবার মূল্য পরিশোধ করুন' },
  'view_details': { en: 'View Details', bn: 'বিস্তারিত দেখুন' },
  'transaction_id': { en: 'Transaction ID', bn: 'লেনদেন নম্বর' },

  // ── Header / navigation ─────────────────────────────────────
  'nav_home': { en: 'Home', bn: 'প্রথম পাতা' },
  'nav_search': { en: 'Search Trips', bn: 'টিকিট খুঁজুন' },
  'nav_routes': { en: 'Routes & Fleet', bn: 'চলাচলের পথ ও বাস' },
  'nav_counters': { en: 'Counters', bn: 'কাউন্টার' },
  'nav_notices': { en: 'Notices', bn: 'ভ্রমণ বিজ্ঞপ্তি' },
  'nav_manage': { en: 'Manage Booking', bn: 'টিকিট পরিচালনা' },
  'nav_tracking': { en: 'Live Tracking', bn: 'বাসের অবস্থান' },
  'nav_support': { en: 'Support', bn: 'সহায়তা' },
  'nav_login': { en: 'Login', bn: 'প্রবেশ করুন' },
  'nav_logout': { en: 'Log out', bn: 'প্রস্থান' },
  'nav_dashboard': { en: 'Dashboard', bn: 'আমার যাত্রা' },
  'nav_profile': { en: 'Profile', bn: 'ব্যক্তিগত তথ্য' },
  'nav_my_bookings': { en: 'My Bookings', bn: 'আমার টিকিট' },
  'toggle_language': { en: 'Toggle language', bn: 'ভাষা পরিবর্তন' },
  'toggle_theme': { en: 'Toggle theme', bn: 'থিম পরিবর্তন' },
  'open_menu': { en: 'Open menu', bn: 'মেনু খুলুন' },
  'close_menu': { en: 'Close menu', bn: 'মেনু বন্ধ করুন' },
  'account_menu': { en: 'Account menu', bn: 'অ্যাকাউন্ট মেনু' },

  // ── Footer ──────────────────────────────────────────────────
  'footer_tagline': {
    en: "Bangladesh's premium intercity coach service. Travel in comfort, arrive with confidence.",
    bn: 'বাংলাদেশের প্রিমিয়াম আন্তঃজেলা বাস সার্ভিস। আরামে ভ্রমণ করুন, নিশ্চিন্তে পৌঁছান।',
  },
  'footer_quick_links': { en: 'Quick Links', bn: 'প্রয়োজনীয় পাতা' },
  'footer_support_legal': { en: 'Support & Legal', bn: 'সহায়তা ও আইনি' },
  'footer_contact': { en: 'Contact', bn: 'যোগাযোগ' },
  'footer_travel_updates': { en: 'Travel Updates', bn: 'ভ্রমণ সংবাদ' },
  'footer_help_center': { en: 'Help Center', bn: 'সহায়তা কেন্দ্র' },
  'footer_terms': { en: 'Terms of Service', bn: 'সেবার শর্তাবলি' },
  'footer_privacy': { en: 'Privacy Policy', bn: 'গোপনীয়তা নীতি' },
  'footer_contact_us': { en: 'Contact Us', bn: 'যোগাযোগ করুন' },
  'footer_address': {
    en: 'Star Line Complex, 314/1, Shaheed Shahidullah Kaisar Road, Feni',
    bn: 'স্টার লাইন কমপ্লেক্স, ৩১৪/১, শহীদ শহীদুল্লাহ কায়সার রোড, ফেনী',
  },
  'footer_rights': { en: 'All rights reserved.', bn: 'সর্বস্বত্ব সংরক্ষিত।' },
  'footer_follow_us': { en: 'Follow Us', bn: 'সামাজিক মাধ্যমে' },
  'footer_developed_by': { en: 'Developed by', bn: 'কারিগরি সহযোগিতায়' },

  // ── Homepage ────────────────────────────────────────────────
  'home_eyebrow': { en: 'Star Line Group', bn: 'স্টার লাইন গ্রুপ' },
  'home_hero_line1': { en: 'Your Journey,', bn: 'আপনার যাত্রা,' },
  'home_hero_line2': { en: 'Our Pride.', bn: 'আমাদের গর্ব।' },
  'home_hero_sub': {
    en: 'Book intercity bus tickets with Star Line — premium coaches, live tracking, and reliable schedules across Bangladesh.',
    bn: 'স্টার লাইনের সাথে আন্তঃজেলা বাসের টিকিট বুক করুন — প্রিমিয়াম কোচ, লাইভ ট্র্যাকিং ও নির্ভরযোগ্য সময়সূচি, সারা বাংলাদেশ জুড়ে।',
  },
  'home_explore_routes': { en: 'Explore Routes', bn: 'রুট দেখুন' },
  'home_verified_operator': { en: 'Verified Operator', bn: 'যাচাইকৃত পরিবহন' },
  'home_passengers': { en: 'Passengers', bn: 'যাত্রী' },
  'home_live_seats': { en: 'Live Seat Availability', bn: 'রিয়েল-টাইম সিট খালি আছে কিনা দেখুন' },
  'home_why': { en: 'Why Star Line', bn: 'কেন স্টার লাইন' },
  'home_why_title': { en: 'A Better Way to Travel', bn: 'ভ্রমণের আরও ভালো উপায়' },
  'home_why_sub': {
    en: 'Premium service, dependable schedules, and a world-class booking experience.',
    bn: 'প্রিমিয়াম সেবা, নির্ভরযোগ্য সময়সূচি এবং বিশ্বমানের বুকিং অভিজ্ঞতা।',
  },
  'home_stat_routes': { en: 'Active Routes', bn: 'সক্রিয় রুট' },
  'showcase_eyebrow': { en: 'Star Line Group', bn: 'স্টার লাইন গ্রুপ' },
  'showcase_title': { en: 'More from Star Line', bn: 'স্টার লাইনের আরও সেবা' },
  'showcase_sub': {
    en: 'Our sister concerns and the latest offers across the Star Line network.',
    bn: 'স্টার লাইন পরিবারের অন্যান্য প্রতিষ্ঠান এবং চলমান বিশেষ অফারসমূহ।',
  },
  'showcase_play': { en: 'Play video', bn: 'ভিডিও চালান' },

  // ── Counters page ──
  'ctr_eyebrow': { en: 'Network & Counters', bn: 'নেটওয়ার্ক ও কাউন্টার' },
  'ctr_title_1': { en: 'Our', bn: 'আমাদের' },
  'ctr_title_2': { en: 'Terminals & Routes', bn: 'টার্মিনাল ও রুট' },
  'ctr_intro': {
    en: "Explore Star Line's complete counter network across Bangladesh. Select a terminal to see all available routes and every stop along the way.",
    bn: 'সারা বাংলাদেশে স্টার লাইনের কাউন্টার নেটওয়ার্ক দেখুন। যেকোনো টার্মিনাল বেছে নিলে সেখান থেকে ছেড়ে যাওয়া রুট ও পথের প্রতিটি স্টপ দেখতে পাবেন।',
  },
  'ctr_search_terminals': { en: 'Search terminals by name or district…', bn: 'টার্মিনালের নাম বা জেলা লিখে খুঁজুন…' },
  'ctr_search_counters': { en: 'Search counters by name, district or phone…', bn: 'কাউন্টারের নাম, জেলা বা ফোন নম্বর দিয়ে খুঁজুন…' },
  'ctr_search_all': { en: 'Search the network by name, district or phone…', bn: 'নাম, জেলা বা ফোন নম্বর দিয়ে নেটওয়ার্কে খুঁজুন…' },
  'ctr_stat_terminals': { en: 'Terminals', bn: 'টার্মিনাল' },
  'ctr_stat_points': { en: 'Counter Points', bn: 'কাউন্টার' },
  'ctr_stat_routes': { en: 'Active Routes', bn: 'সক্রিয় রুট' },
  'ctr_stat_districts': { en: 'Districts Covered', bn: 'জেলা জুড়ে সেবা' },
  'ctr_tab_terminals': { en: 'Terminals', bn: 'টার্মিনাল' },
  'ctr_tab_counters': { en: 'Counters', bn: 'কাউন্টার' },
  'ctr_tab_all': { en: 'All', bn: 'সব' },
  'ctr_view_label': { en: 'Network view', bn: 'নেটওয়ার্ক ভিউ' },
  'ctr_districts': { en: 'Districts', bn: 'জেলা' },
  'ctr_division': { en: 'Division', bn: 'বিভাগ' },
  'ctr_no_terminals': { en: 'No terminals found', bn: 'কোনো টার্মিনাল পাওয়া যায়নি' },
  'ctr_no_districts': { en: 'No districts found', bn: 'কোনো জেলা পাওয়া যায়নি' },
  'ctr_select_terminal': { en: 'Select a Terminal', bn: 'একটি টার্মিনাল বেছে নিন' },
  'ctr_select_terminal_desc': {
    en: 'Choose a terminal from the left to explore available routes and all counter stops along the way.',
    bn: 'বাঁ পাশ থেকে একটি টার্মিনাল বেছে নিন — সেখান থেকে ছেড়ে যাওয়া রুট ও পথের সব কাউন্টার দেখতে পাবেন।',
  },
  'ctr_select_district': { en: 'Select a District', bn: 'একটি জেলা বেছে নিন' },
  'ctr_select_district_counters': {
    en: 'Choose a district from the left to browse every counter there.',
    bn: 'বাঁ পাশ থেকে একটি জেলা বেছে নিন — সেখানকার সব কাউন্টার দেখতে পাবেন।',
  },
  'ctr_select_district_all': {
    en: 'Choose a district from the left to browse every terminal and counter there.',
    bn: 'বাঁ পাশ থেকে একটি জেলা বেছে নিন — সেখানকার সব টার্মিনাল ও কাউন্টার দেখতে পাবেন।',
  },
  'ctr_routes_from': { en: 'Routes from', bn: 'যে রুটগুলো ছাড়ে —' },
  'ctr_no_routes': { en: 'No routes available from this terminal yet.', bn: 'এই টার্মিনাল থেকে এখনো কোনো রুট যুক্ত করা হয়নি।' },
  'ctr_stops': { en: 'stops', bn: 'টি স্টপ' },
  'ctr_stops_along': { en: 'stops along this route', bn: 'টি স্টপ এই রুটে' },
  'ctr_main_terminal': { en: 'Main Terminal', bn: 'প্রধান টার্মিনাল' },
  'ctr_rest_break': { en: '20 min rest break', bn: '২০ মিনিটের যাত্রাবিরতি' },
  'ctr_legend': { en: 'Legend', bn: 'সংকেত' },
  'ctr_starting_point': { en: 'Starting Point', bn: 'যাত্রা শুরু' },
  'ctr_counter_stop': { en: 'Counter Stop', bn: 'কাউন্টার' },
  'ctr_rest_break_short': { en: 'Rest Break', bn: 'যাত্রাবিরতি' },
  'ctr_last_stop': { en: 'Last Stop / Terminal', bn: 'শেষ স্টপ / টার্মিনাল' },
  'ctr_status_active': { en: 'Active', bn: 'সচল' },
  'ctr_status_unverified': { en: 'Unverified', bn: 'অযাচাইকৃত' },
  'ctr_status_unconfirmed': { en: 'Unconfirmed', bn: 'অনিশ্চিত' },
  'ctr_loading': { en: 'Loading counters…', bn: 'কাউন্টার লোড হচ্ছে…' },
  'ctr_counter_one': { en: 'counter', bn: 'কাউন্টার' },
  'ctr_counter_many': { en: 'counters', bn: 'কাউন্টার' },
  'ctr_place_one': { en: 'place', bn: 'স্থান' },
  'ctr_place_many': { en: 'places', bn: 'স্থান' },
  'ctr_nothing_here': { en: 'Nothing listed here yet.', bn: 'এখানে এখনো কিছু যুক্ত করা হয়নি।' },
  'ctr_no_match': { en: 'No results match your search here.', bn: 'আপনার খোঁজার সাথে এখানে কিছু মেলেনি।' },

  // 404 strings live with the Not Found block at the end of this file. They were
  // declared twice; a later duplicate key silently replaces the earlier one, so
  // this pair was dead weight that only tsc could see.
  'nf_home': { en: 'Back to home', bn: 'প্রথম পাতায় ফিরুন' },
  'nf_search': { en: 'Search trips', bn: 'বাসের টিকিট খুঁজুন' },
  'home_stat_terminals': { en: 'Main Terminals', bn: 'প্রধান টার্মিনাল' },
  'home_stat_counters': { en: 'Counters', bn: 'কাউন্টার' },
  'home_stat_districts': { en: 'Districts Covered', bn: 'জেলা জুড়ে সেবা' },
  'feat_tracking': { en: 'Live Tracking', bn: 'লাইভ ট্র্যাকিং' },
  'feat_tracking_desc': {
    en: 'Track your coach in real-time from departure to destination',
    bn: 'ছাড়ার সময় থেকে গন্তব্য পর্যন্ত রিয়েল-টাইমে আপনার বাস ট্র্যাক করুন',
  },
  'feat_fleet': { en: 'Premium Fleet', bn: 'প্রিমিয়াম বহর' },
  'feat_fleet_desc': {
    en: 'AC & Non-AC coaches with modern amenities',
    bn: 'আধুনিক সুবিধাসহ এসি ও নন-এসি কোচ',
  },
  'feat_seat': { en: 'Seat Selection', bn: 'সিট বাছাই' },
  'feat_seat_desc': {
    en: 'Choose your preferred seat with interactive coach maps',
    bn: 'ইন্টারঅ্যাকটিভ সিট ম্যাপ থেকে পছন্দের সিট বেছে নিন',
  },
  'feat_pay': { en: 'Easy Payments', bn: 'সহজ পেমেন্ট' },
  'feat_pay_desc': {
    en: 'Pay with bKash, Nagad, card, or at counter',
    bn: 'বিকাশ, নগদ, কার্ড অথবা কাউন্টারে পেমেন্ট করুন',
  },
  'feat_ontime': { en: 'On-Time Promise', bn: 'সময়ের নিশ্চয়তা' },
  // Was "94% on-time performance across all routes". No on-time rate is
  // measured anywhere in the data, so the figure was invented (AGENTS.md: no
  // fabricated metrics). Replaced with what the product actually does; restore
  // a percentage only once it is computed from trip_tracking.
  'feat_ontime_desc': {
    en: 'Fixed departure times, with live trip status you can check any time',
    bn: 'নির্ধারিত সময়ে যাত্রা, আর যেকোনো সময় বাসের অবস্থান দেখার সুবিধা',
  },
  'feat_safe': { en: 'Safe & Insured', bn: 'নিরাপদ ও বিমাকৃত' },
  'feat_safe_desc': {
    en: 'GPS tracked coaches with full passenger insurance',
    bn: 'জিপিএস ট্র্যাকিংসহ কোচ ও সম্পূর্ণ যাত্রী বিমা',
  },
  'home_popular': { en: 'Popular', bn: 'জনপ্রিয়' },
  'home_top_routes': { en: 'Top Routes', bn: 'জনপ্রিয় রুট' },
  'home_all_routes': { en: 'All routes', bn: 'সব রুট' },
  'home_ready': { en: 'Ready to Travel?', bn: 'যাত্রার জন্য প্রস্তুত?' },
  'home_book_next': { en: 'Book Your Next Trip', bn: 'পরবর্তী যাত্রা বুক করুন' },
  'home_book_next_sub': {
    en: 'Experience premium intercity travel with Star Line. Your seat is waiting.',
    bn: 'স্টার লাইনের সাথে প্রিমিয়াম আন্তঃজেলা ভ্রমণের অভিজ্ঞতা নিন। আপনার সিট অপেক্ষা করছে।',
  },

  // ── Support / Help Center ───────────────────────────────────
  'help_center': { en: 'Help Center', bn: 'সহায়তা কেন্দ্র' },
  'help_sub': {
    en: 'Find answers to common questions or get in touch with our support team',
    bn: 'সাধারণ প্রশ্নের উত্তর খুঁজুন অথবা আমাদের সহায়তা দলের সাথে যোগাযোগ করুন',
  },
  'help_call': { en: 'Call Us', bn: 'কল করুন' },
  'help_hotline_247': { en: '24/7 Hotline', bn: '২৪/৭ হটলাইন' },
  'help_email': { en: 'Email', bn: 'ইমেইল' },
  'help_reply_2h': { en: 'Reply within 2 hours', bn: '২ ঘণ্টার মধ্যে উত্তর' },
  'help_live_chat': { en: 'Live Chat', bn: 'সরাসরি বার্তা' },
  'help_start_chat': { en: 'Start Chat', bn: 'কথা শুরু করুন' },
  'help_chat_hours': { en: 'Available 8AM-12AM', bn: 'সকাল ৮টা - রাত ১২টা' },
  'faq_cat_booking': { en: 'Booking', bn: 'টিকিট সংরক্ষণ' },
  'faq_cat_refunds': { en: 'Cancellation & Refunds', bn: 'বাতিল ও টাকা ফেরত' },
  'faq_cat_baggage': { en: 'Baggage & Boarding', bn: 'মালপত্র ও বাসে ওঠা' },
  'faq_cat_trip': { en: 'On the Trip', bn: 'যাত্রাপথে' },
  'faq_q_book': { en: 'How do I book a ticket?', bn: 'কীভাবে টিকিট কাটব?' },
  'faq_a_book': {
    en: 'Use the search form on our homepage to find trips. Select your preferred bus, choose seats, enter passenger details, and complete the payment.',
    bn: 'প্রথম পাতার খোঁজার ঘরে যাত্রার স্থান ও তারিখ দিন। পছন্দের বাস ও আসন বেছে নিয়ে যাত্রীর তথ্য লিখুন, তারপর মূল্য পরিশোধ করুন।',
  },
  'faq_q_multi': { en: 'Can I book for multiple passengers?', bn: 'একসাথে একাধিক যাত্রীর টিকিট কাটা যায়?' },
  'faq_a_multi': {
    en: 'Yes, select the number of passengers in the search form. Each person may book up to 3 tickets on a departure.',
    bn: 'হ্যাঁ। খোঁজার সময় যাত্রীর সংখ্যা বেছে নিন। একটি যাত্রার জন্য একজন সর্বোচ্চ ৩টি টিকিট কাটতে পারবেন।',
  },
  'faq_q_payment': { en: 'What payment methods are accepted?', bn: 'কোন কোন মাধ্যমে মূল্য পরিশোধ করা যায়?' },
  'faq_a_payment': {
    en: 'We accept bKash, Nagad, credit/debit cards (Visa, Mastercard), and cash payment at counter.',
    bn: 'bKash, Nagad, Visa বা Mastercard-এর ক্রেডিট/ডেবিট কার্ড এবং কাউন্টারে নগদ অর্থ গ্রহণ করা হয়।',
  },
  'faq_q_cancel': { en: 'What is the cancellation policy?', bn: 'বাতিলের নিয়ম কী?' },
  'faq_a_cancel': {
    en: 'Full refund if cancelled 24+ hours before departure (৳50 processing fee). 50% refund between 6–24 hours. 25% refund between 2–6 hours. No refund within 2 hours of departure. Service fees are non-refundable.',
    bn: 'বাস ছাড়ার ২৪ ঘণ্টার বেশি আগে বাতিল করলে ৫০ টাকা প্রক্রিয়াকরণ খরচ কেটে বাকি ভাড়া ফেরত দেওয়া হবে। ৬–২৪ ঘণ্টা আগে ৫০%, ২–৬ ঘণ্টা আগে ২৫% ভাড়া ফেরত পাবেন। বাস ছাড়ার ২ ঘণ্টার মধ্যে বাতিল করলে টাকা ফেরত দেওয়া হবে না। সেবা মূল্য ফেরতযোগ্য নয়।',
  },
  'faq_q_refund_time': { en: 'How long do refunds take?', bn: 'টাকা ফেরত পেতে কত সময় লাগে?' },
  'faq_a_refund_time': {
    en: 'Refunds to bKash/Nagad take 1-3 business days. Card refunds take 5-7 business days.',
    bn: 'bKash বা Nagad-এ টাকা ফিরতে ১–৩ কর্মদিবস এবং কার্ডে ফিরতে ৫–৭ কর্মদিবস সময় লাগে।',
  },
  'faq_q_reschedule': { en: 'Can I reschedule my trip?', bn: 'যাত্রার তারিখ পরিবর্তন করা যায়?' },
  'faq_a_reschedule': {
    en: 'Yes, you can reschedule up to 4 hours before departure for free (subject to seat availability on the new trip).',
    bn: 'হ্যাঁ। নতুন যাত্রায় আসন খালি থাকলে বাস ছাড়ার ৪ ঘণ্টা আগ পর্যন্ত বিনা খরচে সময়সূচি পরিবর্তন করা যাবে।',
  },
  'faq_q_baggage': { en: 'What is the baggage allowance?', bn: 'কতটুকু মালপত্র নেওয়া যাবে?' },
  'faq_a_baggage': {
    en: 'Each passenger is allowed 2 bags (max 20kg each) in the luggage compartment plus 1 carry-on bag.',
    bn: 'প্রত্যেক যাত্রী মালপত্র রাখার স্থানে সর্বোচ্চ ২০ কেজি ওজনের ২টি ব্যাগ এবং সঙ্গে ১টি ছোট হাতব্যাগ নিতে পারবেন।',
  },
  'faq_q_arrive': { en: 'How early should I arrive?', bn: 'কতক্ষণ আগে পৌঁছাতে হবে?' },
  'faq_a_arrive': {
    en: 'We recommend arriving at the boarding point at least 15 minutes before the scheduled departure time.',
    bn: 'নির্ধারিত সময়ের অন্তত ১৫ মিনিট আগে বাসে ওঠার স্থানে পৌঁছানোর অনুরোধ করছি।',
  },
  'faq_q_id': { en: 'What ID do I need?', bn: 'কী পরিচয়পত্র লাগবে?' },
  'faq_a_id': {
    en: 'Carry a valid National ID card, passport, or any government-issued photo ID.',
    bn: 'বৈধ জাতীয় পরিচয়পত্র, পাসপোর্ট বা সরকার-প্রদত্ত ছবিযুক্ত পরিচয়পত্র সঙ্গে রাখুন।',
  },
  'faq_q_wifi': { en: 'Is WiFi available on board?', bn: 'বাসে কি ওয়াইফাই আছে?' },
  'faq_a_wifi': {
    en: 'WiFi is available on Platinum and Gold coaches. Silver and Express coaches do not have WiFi.',
    bn: 'Platinum ও Gold কোচে WiFi রয়েছে। Silver ও Express কোচে WiFi সুবিধা নেই।',
  },
  'faq_q_rest': { en: 'Are there rest stops?', bn: 'পথে কি বিরতি আছে?' },
  'faq_a_rest': {
    en: 'Trips over 4 hours include a 15-20 minute rest stop at designated locations.',
    bn: '৪ ঘণ্টার বেশি যাত্রায় নির্ধারিত স্থানে ১৫–২০ মিনিটের বিশ্রাম বিরতি থাকে।',
  },
  'faq_q_track': { en: 'Can I track my bus in real-time?', bn: 'বাসের বর্তমান অবস্থান কি দেখা যাবে?' },
  'faq_a_track': {
    en: 'Yes! Use the Live Tracking feature on our website to track your coach\'s location and ETA in real-time.',
    bn: 'হ্যাঁ। ওয়েবসাইটের “বাসের অবস্থান” পাতায় গিয়ে আপনার বাস কোথায় আছে এবং কখন পৌঁছাবে তা দেখতে পারবেন।',
  },

  // ── Counters ────────────────────────────────────────────────
  'counters_eyebrow': { en: 'Network & Counters', bn: 'নেটওয়ার্ক ও কাউন্টার' },
  'counters_title_our': { en: 'Our', bn: 'আমাদের' },
  'counters_title_rest': { en: 'Terminals & Routes', bn: 'টার্মিনাল ও রুট' },
  'counters_sub': {
    en: "Explore Star Line's complete counter network across Bangladesh. Select a terminal to see all available routes and every stop along the way.",
    bn: 'সারা বাংলাদেশে স্টার লাইনের সম্পূর্ণ কাউন্টার নেটওয়ার্ক দেখুন। রুট ও পথের সব স্টপ দেখতে একটি টার্মিনাল নির্বাচন করুন।',
  },
  'counters_search_ph': { en: 'Search terminals by name or district...', bn: 'নাম বা জেলা দিয়ে টার্মিনাল খুঁজুন...' },
  'counters_terminals': { en: 'Terminals', bn: 'টার্মিনাল' },
  'counters_counter_points': { en: 'Counter Points', bn: 'কাউন্টার পয়েন্ট' },
  'counters_active_routes': { en: 'Active Routes', bn: 'সক্রিয় রুট' },
  'counters_districts': { en: 'Districts Covered', bn: 'জেলা' },
  'counters_all': { en: 'All Counters', bn: 'সব কাউন্টার' },
  'counters_divisions': { en: 'Divisions', bn: 'বিভাগ' },
  'counters_select_terminal': { en: 'Select a Terminal', bn: 'একটি টার্মিনাল নির্বাচন করুন' },
  'counters_select_terminal_desc': {
    en: 'Choose a terminal from the left to explore available routes and all counter stops along the way.',
    bn: 'রুট ও পথের সব কাউন্টার দেখতে বাঁ পাশ থেকে একটি টার্মিনাল বেছে নিন।',
  },
  'counters_select_division': { en: 'Select a Division', bn: 'একটি বিভাগ নির্বাচন করুন' },
  'counters_select_division_desc': {
    en: 'Choose a division from the left to browse all counters in that area.',
    bn: 'ঐ এলাকার সব কাউন্টার দেখতে বাঁ পাশ থেকে একটি বিভাগ বেছে নিন।',
  },
  'counters_none_found': { en: 'No terminals found', bn: 'কোনো টার্মিনাল পাওয়া যায়নি' },
  'counters_legend': { en: 'Legend', bn: 'নির্দেশিকা' },
  'counters_in_division': { en: 'counters in this division', bn: 'টি কাউন্টার এই বিভাগে' },
  'counters_stops_on_route': { en: 'stops on this route', bn: 'টি স্টপ এই রুটে' },

  // ── Routes & Fleet ──────────────────────────────────────────
  'routes_eyebrow': { en: 'Star Line Network', bn: 'স্টার লাইনের সেবা এলাকা' },
  'routes_title': { en: 'Routes & Fleet', bn: 'চলাচলের পথ ও বাস' },
  'routes_sub': {
    en: "Explore Star Line's intercity route network, terminal stops, and premium coach fleet.",
    bn: 'স্টার লাইনের আন্তঃজেলা চলাচলের পথ, পথে থাকা কাউন্টার ও আরামদায়ক বাসগুলো দেখুন।',
  },
  'routes_explorer': { en: 'Route Explorer', bn: 'চলাচলের পথ দেখুন' },
  'routes_count': { en: 'routes', bn: 'টি চলাচলের পথ' },
  'routes_stops': { en: 'stops', bn: 'টি বিরতি' },
  'routes_popular': { en: 'Popular Routes', bn: 'জনপ্রিয় চলাচলের পথ' },
  'routes_our_fleet': { en: 'Our Fleet', bn: 'আমাদের বাস' },
  'routes_fleet_sub': {
    en: 'Premium coaches built for comfort and safety',
    bn: 'আরাম ও নিরাপত্তার কথা মাথায় রেখে প্রস্তুত উন্নতমানের বাস',
  },
  'routes_seats': { en: 'seats', bn: 'টি আসন' },
  'routes_safety': { en: 'Safety & Reliability', bn: 'নিরাপত্তা ও নির্ভরযোগ্যতা' },
  'routes_gps': { en: 'GPS Tracked', bn: 'GPS-এ অবস্থান দেখা যায়' },
  'routes_gps_desc': {
    en: 'All coaches are GPS tracked 24/7 for your safety',
    bn: 'যাত্রীদের নিরাপত্তার জন্য সব বাসের অবস্থান সার্বক্ষণিক GPS-এ দেখা হয়',
  },
  'routes_drivers': { en: 'Trained Drivers', bn: 'প্রশিক্ষিত চালক' },
  'routes_drivers_desc': {
    en: 'Professional drivers with 10+ years experience',
    bn: '১০ বছরের বেশি অভিজ্ঞতাসম্পন্ন পেশাদার চালক',
  },
  'routes_maintenance': { en: 'Regular Maintenance', bn: 'নিয়মিত রক্ষণাবেক্ষণ' },
  'routes_maintenance_desc': {
    en: 'Coaches serviced every 5,000 km',
    bn: 'প্রতি ৫,০০০ কিলোমিটার চলার পর বাসের পূর্ণাঙ্গ রক্ষণাবেক্ষণ করা হয়',
  },
  'routes_insurance': { en: 'Insurance Coverage', bn: 'বিমা সুরক্ষা' },
  'routes_insurance_desc': {
    en: 'Full passenger insurance on every trip',
    bn: 'প্রতিটি যাত্রায় সম্পূর্ণ যাত্রী বিমা',
  },
  'routes_map_loading': { en: 'Loading map…', bn: 'মানচিত্র প্রস্তুত হচ্ছে…' },
  'routes_map_unavailable': { en: 'Map Unavailable', bn: 'মানচিত্র পাওয়া যাচ্ছে না' },
  'routes_map_unavailable_desc': { en: 'Unable to load map tiles', bn: 'মানচিত্রের তথ্য দেখানো যায়নি' },

  // ── Notices ─────────────────────────────────────────────────
  'notices_title': { en: 'Travel Updates', bn: 'ভ্রমণ আপডেট' },
  'notices_sub': {
    en: 'Service notices, schedule changes, and important announcements',
    bn: 'সেবা সংক্রান্ত নোটিশ, সময়সূচি পরিবর্তন ও গুরুত্বপূর্ণ ঘোষণা',
  },
  'notices_none': { en: 'No notices right now', bn: 'এই মুহূর্তে কোনো নোটিশ নেই' },
  'notices_none_desc': {
    en: 'There are no travel updates at the moment. Please check back later.',
    bn: 'এই মুহূর্তে কোনো ভ্রমণ আপডেট নেই। পরে আবার দেখুন।',
  },
  'notices_read_more': { en: 'Read more', bn: 'বিস্তারিত পড়ুন' },
  'notices_back': { en: 'Back to Notices', bn: 'নোটিশে ফিরুন' },

  // ── Manage Booking ──────────────────────────────────────────
  'manage_title': { en: 'Manage Booking', bn: 'বুকিং ব্যবস্থাপনা' },
  'manage_sub': {
    en: 'Look up your booking to view details, cancel, or resend your ticket',
    bn: 'বিস্তারিত দেখতে, বাতিল করতে বা টিকিট আবার পাঠাতে আপনার বুকিং খুঁজুন',
  },
  'manage_booking_id_ph': { en: 'Paste your Booking ID', bn: 'আপনার বুকিং আইডি দিন' },
  'manage_phone_optional': { en: 'Phone (optional)', bn: 'ফোন (ঐচ্ছিক)' },
  'manage_lookup': { en: 'Look Up Booking', bn: 'বুকিং খুঁজুন' },
  'manage_searching': { en: 'Searching...', bn: 'খোঁজা হচ্ছে...' },
  'manage_not_found': {
    en: 'No booking found with that ID. Please check and try again.',
    bn: 'এই আইডিতে কোনো বুকিং পাওয়া যায়নি। আইডিটি মিলিয়ে আবার চেষ্টা করুন।',
  },
  'manage_route': { en: 'Route', bn: 'রুট' },
  'manage_date_time': { en: 'Date & Time', bn: 'তারিখ ও সময়' },
  'manage_seats': { en: 'Seats', bn: 'সিট' },
  'manage_total_paid': { en: 'Total Paid', bn: 'পরিশোধিত' },
  'manage_total_due': { en: 'Total Due', bn: 'বকেয়া' },
  'manage_status': { en: 'Booking Status', bn: 'বুকিংয়ের অবস্থা' },
  'manage_status_pending': {
    en: 'Booking is pending. No refund request pending.',
    bn: 'বুকিংটি পেন্ডিং আছে। কোনো রিফান্ড অনুরোধ নেই।',
  },
  'manage_status_cancelled': { en: 'This booking has been cancelled.', bn: 'এই বুকিংটি বাতিল করা হয়েছে।' },
  'manage_status_confirmed': { en: 'This booking is confirmed.', bn: 'এই বুকিংটি নিশ্চিত করা হয়েছে।' },

  // ── Live Tracking ───────────────────────────────────────────
  'tracking_title': { en: 'Live Trip Tracking', bn: 'লাইভ ট্রিপ ট্র্যাকিং' },
  'tracking_sub': { en: 'Real-time status for your trip', bn: 'আপনার যাত্রার রিয়েল-টাইম অবস্থা' },
  'tracking_track_bus': { en: 'Track Your Bus', bn: 'আপনার বাস ট্র্যাক করুন' },
  'tracking_enter_pnr': { en: 'Enter your booking reference to see live trip status', bn: 'লাইভ অবস্থা দেখতে আপনার বুকিং রেফারেন্স দিন' },
  'tracking_track': { en: 'Track', bn: 'ট্র্যাক করুন' },
  'tracking_none': {
    en: 'No live tracking available for this booking. The bus may not have departed yet.',
    bn: 'এই বুকিংয়ের জন্য লাইভ ট্র্যাকিং নেই। বাসটি হয়তো এখনো ছাড়েনি।',
  },

  // ── Auth ────────────────────────────────────────────────────
  'auth_welcome_back': { en: 'Welcome Back', bn: 'আবার স্বাগতম' },
  'auth_signin': { en: 'Sign in', bn: 'লগইন করুন' },
  'auth_signin_sub': { en: 'Enter your credentials to continue', bn: 'চালিয়ে যেতে আপনার তথ্য দিন' },
  'auth_or_email': { en: 'or continue with email', bn: 'অথবা ইমেইল দিয়ে চালিয়ে যান' },
  'auth_email_label': { en: 'Email address', bn: 'ইমেইল ঠিকানা' },
  'auth_password': { en: 'Password', bn: 'পাসওয়ার্ড' },
  'auth_forgot': { en: 'Forgot?', bn: 'ভুলে গেছেন?' },
  'auth_show_password': { en: 'Show password', bn: 'পাসওয়ার্ড দেখান' },
  'auth_hide_password': { en: 'Hide password', bn: 'পাসওয়ার্ড লুকান' },
  'auth_no_account': { en: "Don't have an account?", bn: 'অ্যাকাউন্ট নেই?' },
  'auth_create_one': { en: 'Create one', bn: 'তৈরি করুন' },
  'auth_have_account': { en: 'Already have an account?', bn: 'আগে থেকেই অ্যাকাউন্ট আছে?' },
  'auth_signup': { en: 'Create account', bn: 'অ্যাকাউন্ট তৈরি করুন' },
  'auth_agree': { en: 'By signing in you agree to our', bn: 'লগইন করলে আপনি সম্মত হচ্ছেন আমাদের' },

  // ── Not Found ───────────────────────────────────────────────
  'nf_title': { en: 'Page Not Found', bn: 'পৃষ্ঠাটি পাওয়া যায়নি' },
  'nf_desc': {
    en: "The page you're looking for doesn't exist or has moved.",
    bn: 'আপনি যে পৃষ্ঠাটি খুঁজছেন সেটি নেই অথবা সরিয়ে ফেলা হয়েছে।',
  },
};
